/**
 * CTA-ALL-1910: single normalizer for every public commercial link.
 * A link that asks for a quote/order/service and still points to a bare
 * /start, the legacy /quote, the freelance /order wizard or a /contact or
 * /pricing page with a commercial label is rewritten to the /start widget
 * with type/service/industry/page/from/cta_id. Informative contact links,
 * "talk to an expert", "view pricing" and /booking keep their destination.
 */
import { startHref } from './ctaSource';

const PRIVATE_RE = /^\/(admin|agent|[a-z]{2}\/(portal|auth|agent|admin|start|order|booking))(\/|$)/;
const QUOTE_RE = /quote|devis|estimate|assessment|get started|order|commander|demander|request|hire|buy|acheter|angebot|presupuesto|preventivo|orçamento|offerte|عرض|اطلب|ابدأ/i;
const CONTACT_INTENT_RE = /expert|call|appel|discuter|discuss|talk|speak|book|réserver|consult|reference|référence|contact us|contactez|اتصل|message/i;
const VIEW_PRICING_RE = /(voir|view|see|compare|comparer|ansehen|ver|bekijk)\b.*(tarif|pricing|prices|prix|preise|precios|prijzen)/i;
const REQUIRED = ['type', 'from', 'page', 'cta_id'];
const KEEP = ['type', 'service', 'industry', 'channel', 'offer', 'package', 'languages', 'agents', 'coverage', 'from', 'cta_id'];

export type CtaZone = 'header' | 'footer' | 'sticky' | 'main';

function slug(s: string): string {
  const latin = s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32);
  if (latin.length >= 3 || !s) return latin;
  // Non-Latin label (ar, zh, ja...): stable short hash so each CTA stays distinct.
  let h = 5381;
  for (const ch of s) h = ((h * 33) ^ ch.codePointAt(0)!) >>> 0;
  return `l${h.toString(36)}`;
}

/**
 * Returns the /start href a commercial link must use, or null when the link
 * must stay as it is. `pathname` is the current localized path.
 */
export function normalizeCommercialHref(href: string, label: string, pathname: string, zone: CtaZone = 'main'): string | null {
  // Own-domain absolute links (blog bodies) are treated like internal ones.
  href = (href || '').replace(/^https?:\/\/(www\.)?bpo-contact\.com(?=\/)/i, '');
  if (!href || /^(https?:|mailto:|tel:|#|javascript:)/i.test(href)) return null;
  const m = pathname.match(/^\/([a-z]{2})(\/|$)/);
  if (!m || PRIVATE_RE.test(pathname)) return null;
  const lang = m[1];
  const [rawPath, query = ''] = href.split('?');
  const lm = rawPath.match(/^\/([a-z]{2})(\/.*)?$/);
  if (!lm) return null;
  const target = (lm[2] || '/').replace(/\/$/, '') || '/';
  const text = (label || '').trim().replace(/\s+/g, ' ');
  const short = text.length > 0 && text.length <= 48;
  const qs = new URLSearchParams(query);

  let kind: 'start' | 'legacy' | 'order' | null = null;
  if (target === '/start') {
    if (REQUIRED.every((k) => qs.get(k))) return null;
    kind = 'start';
  } else if (target === '/quote' || target === '/get-a-quote') kind = 'legacy';
  else if ((target === '/order' || target === '/freelance/order') && short && QUOTE_RE.test(text)) kind = 'order';
  else if ((target === '/contact' || target === '/pricing' || target === '/tarifs') && short && QUOTE_RE.test(text) && !CONTACT_INTENT_RE.test(text) && !VIEW_PRICING_RE.test(text)) kind = 'legacy';
  if (!kind) return null;

  const from = qs.get('from') || `auto-${zone}`;
  const ctaId = qs.get('cta_id') || `${zone}-${slug(text) || 'cta'}`;
  const base = startHref(lang, pathname, from, ctaId);
  const [b, bq] = base.split('?');
  const out = new URLSearchParams(bq);
  // INC-1910: a legacy freelance-order quote link carries no service intent of
  // its own — the type comes from the page (a BPO page stays bpo) and no
  // service is guessed from it.
  if (kind === 'order') {
    out.delete('service');
    // Freelance marketplace pages request digital/IT work.
    if (/^\/[a-z]{2}\/(freelance|freelancers|marketplace)(\/|$)/.test(pathname)) out.set('type', 'it');
  }
  // A bare /start link (no type of its own) on a page that gives no service or
  // sector hint must keep the BPO-vs-IT picker instead of forcing BPO.
  if (kind === 'start' && !qs.get('type') && !out.get('service') && !out.get('industry') && out.get('type') === 'bpo'
    && !/^\/[a-z]{2}\/(services|call-center|industries)([/-]|$)/.test(pathname)) {
    out.delete('type');
  }
  // Explicit context carried by the link always wins over the inferred one.
  KEEP.forEach((k) => { const v = qs.get(k); if (v) out.set(k, v); });
  return `${b}?${out.toString()}`;
}
